/**
 * The pane's assistant-ui runtime over Aegra.
 *
 * A cut-down `useChatRuntime` from the web app. Same primitives
 * (`useLangGraphRuntime`, the LangGraph SDK client, the thread-list adapter,
 * the streamed-message dedupe and summarisation filters) and the same run
 * payload, so the backend cannot tell the surfaces apart except by the
 * metadata they stamp. Left out on purpose: checkpoint editing, the
 * IndexedDB thread cache, background-run reconnection and the async-task
 * taps. A task pane shows one conversation and its runs are short; each of
 * those would be carried weight here until the frontend moves into this
 * repository and the two hooks converge.
 */
import type { AttachmentAdapter, FeedbackAdapter } from "@assistant-ui/react";
import {
  type LangChainMessage,
  type LangGraphInterruptState,
  type LangGraphStreamCallback,
  useLangGraphRuntime
} from "@assistant-ui/react-langgraph";
import { Client } from "@langchain/langgraph-sdk";
import { useCallback, useMemo, useRef, useState } from "react";

import {
  beginUserAction,
  buildAgentClientOptions,
  buildRunMetadata,
  type UnauthorizedResolver
} from "@/agents-v2/chat-runtime/agent-client";
import { dedupeUpdatesMessages } from "@/agents-v2/chat-runtime/dedupe-updates-messages";
import { classifyRunError, logRunError } from "@/agents-v2/chat-runtime/run-error";
import {
  compactionFromValues,
  type ContextCompaction,
  filterSummarizationStream,
  sanitizeSummarizationValues
} from "@/agents-v2/chat-runtime/summarization-stream";
import { createThreadListAdapter } from "@/agents-v2/chat-runtime/thread-list-adapter";
import { threadStateCache } from "@/agents-v2/chat-runtime/thread-state-cache";
import {
  type AgentThreadMetadata,
  type AgentThreadValues,
  type StreamPayload,
  STREAM_MODE,
  type ThreadMetadataPatcher
} from "@/agents-v2/chat-runtime/types";
import { ensureMetadataOnlyMessagesHaveContent } from "@/agents-v2/message-metadata/metadata-only-message";
import type { AgentUploadedFile } from "@/agents-v2/api";
import { prepareUploadedFileMessages } from "@/agents-v2/uploads/uploaded-files";
import { asRecord } from "@/agents-v2/utils/unknown";

/** The `page` every Word run reports: what PostHog and LangSmith group by. */
export const WORD_PAGE = "word";

export type UseChatRuntimeOptions = {
  assistantId: string;
  apiUrl: string;
  token: string;
  attachmentAdapter?: AttachmentAdapter;
  feedbackAdapter?: FeedbackAdapter;
  /** Run context, read at send time so it reflects the document right then. */
  resolveContext?: () => unknown;
  /** Awaited before each send; the pane refreshes its document snapshot here. */
  onBeforeSend?: () => Promise<void> | void;
  onStreamComplete?: () => Promise<void> | void;
  initialMetadata?: AgentThreadMetadata;
  patchThreadMetadata?: ThreadMetadataPatcher;
  prepareMessages?: (
    threadId: string,
    messages: LangChainMessage[]
  ) => LangChainMessage[];
  threadScopeMetadata?: AgentThreadMetadata;
  resolveSession?: UnauthorizedResolver;
  /** Bid section label for analytics; "" outside a bid. */
  section?: string;
  /** Uploads the files attached to a message into the thread's sandbox. */
  uploadFiles?: (threadId: string, files: readonly File[], signal?: AbortSignal) => Promise<AgentUploadedFile[]>;
};

const toRunConfig = (value: unknown): StreamPayload["config"] | undefined =>
  value && typeof value === "object"
    ? (value as NonNullable<StreamPayload["config"]>)
    : undefined;

const isReconnectSignal = (config: {
  command?: unknown;
  checkpointId?: unknown;
  runConfig?: unknown;
}) =>
  config.command == null &&
  config.checkpointId == null &&
  config.runConfig == null;

/** A run that starts new work, as opposed to a command resuming a paused one. */
export const isDuplicateRunCandidate = (config: { command?: unknown }) =>
  config.command == null;

const emptyStream = () => (async function* () {})();

const toInterrupts = (tasks: unknown): LangGraphInterruptState[] | undefined => {
  if (!Array.isArray(tasks)) return undefined;
  const found: LangGraphInterruptState[] = [];
  for (const task of tasks) {
    const interrupts = asRecord(task).interrupts;
    if (Array.isArray(interrupts)) {
      found.push(...(interrupts as LangGraphInterruptState[]));
    }
  }
  return found.length ? found : undefined;
};

export const useChatRuntime = ({
  assistantId,
  apiUrl,
  token,
  attachmentAdapter,
  feedbackAdapter,
  resolveContext,
  onBeforeSend,
  onStreamComplete,
  initialMetadata,
  patchThreadMetadata,
  prepareMessages,
  threadScopeMetadata,
  resolveSession,
  section = "",
  uploadFiles
}: UseChatRuntimeOptions) => {
  const resolveSessionRef = useRef<UnauthorizedResolver | undefined>(resolveSession);
  resolveSessionRef.current = resolveSession;
  const client = useMemo(
    () =>
      new Client(
        buildAgentClientOptions(apiUrl, token, {
          onUnauthorized: () => resolveSessionRef.current?.() ?? Promise.resolve(null)
        })
      ),
    [apiUrl, token]
  );

  // Everything the stream callback reads lives in refs so the memoised
  // callback never rebuilds mid-run when the caller re-renders.
  const resolveContextRef = useRef(resolveContext);
  const onBeforeSendRef = useRef(onBeforeSend);
  const onStreamCompleteRef = useRef(onStreamComplete);
  const prepareMessagesRef = useRef(prepareMessages);
  const initialMetadataRef = useRef(initialMetadata);
  const threadScopeMetadataRef = useRef(threadScopeMetadata);
  const sectionRef = useRef(section);
  const uploadFilesRef = useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;
  resolveContextRef.current = resolveContext;
  onBeforeSendRef.current = onBeforeSend;
  onStreamCompleteRef.current = onStreamComplete;
  prepareMessagesRef.current = prepareMessages;
  initialMetadataRef.current = initialMetadata;
  threadScopeMetadataRef.current = threadScopeMetadata;
  sectionRef.current = section;

  // One live run per thread: a double-submitted message must not start a
  // second run, but a resume command must always go through (PE-474).
  const inFlightRef = useRef<Set<string>>(new Set());
  const activeThreadIdRef = useRef<string | null>(null);
  const activeRunIdRef = useRef<string | null>(null);

  const [corruptedThreadId, setCorruptedThreadId] = useState<string | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isContextCompacting, setIsContextCompacting] = useState(false);
  // Per thread: where the agent last compacted, for the thread's divider.
  const [compactionByThread, setCompactionByThread] = useState<
    Record<string, ContextCompaction | null>
  >({});
  const recordCompaction = useCallback(
    (threadId: string, compaction: ContextCompaction | null) => {
      setCompactionByThread((previous) => ({ ...previous, [threadId]: compaction }));
    },
    []
  );
  // Threads whose live run compacted. The event itself is not in the
  // `updates` stream (deepagents writes it straight to state), so the run's
  // end triggers one state read to place the divider.
  const compactedThreadsRef = useRef<Set<string>>(new Set());
  // A compaction seen in a node update names a cutoff into the thread's
  // message list without the list; one read of the thread state resolves it.
  const resolveCompaction = useCallback(
    (threadId: string, values: Record<string, unknown> | null) => {
      if (values) {
        recordCompaction(threadId, compactionFromValues(values));
        return;
      }
      void client.threads
        .getState(threadId)
        .then((state) => recordCompaction(threadId, compactionFromValues(state.values)))
        .catch(() => undefined);
    },
    [client, recordCompaction]
  );

  const reportRunError = useCallback(
    (error: unknown, ctx: { threadId: string | null; runId: string | null }) => {
      const { kind } = classifyRunError(error);
      logRunError(error, { assistantId, ...ctx });
      if (kind === "corrupted_history" && ctx.threadId) {
        setCorruptedThreadId(ctx.threadId);
      }
      if (kind === "auth_expired") setSessionExpired(true);
    },
    [assistantId]
  );
  const reportRunErrorRef = useRef(reportRunError);
  reportRunErrorRef.current = reportRunError;

  const stream = useMemo<LangGraphStreamCallback<LangChainMessage>>(
    () => async (messages, config) => {
      const { externalId } = await config.initialize();
      if (!externalId) throw new Error("Thread has not been initialized.");
      activeThreadIdRef.current = externalId;

      // An empty send with no command is the runtime's reconnect probe; the
      // pane does not rejoin background runs, so there is nothing to stream.
      if (messages.length === 0 && isReconnectSignal(config)) {
        return emptyStream();
      }

      if (isDuplicateRunCandidate(config) && inFlightRef.current.has(externalId)) {
        return emptyStream();
      }
      inFlightRef.current.add(externalId);
      if (corruptedThreadId === externalId) setCorruptedThreadId(null);
      const settle = () => inFlightRef.current.delete(externalId);
      config.abortSignal.addEventListener("abort", settle, { once: true });

      try {
        await onBeforeSendRef.current?.();
        const upload = uploadFilesRef.current;
        const uploadedMessages = upload
          ? await prepareUploadedFileMessages(
              messages,
              (files, signal) => upload(externalId, files, signal),
              config.abortSignal
            )
          : messages;
        const prepared = ensureMetadataOnlyMessagesHaveContent(
          prepareMessagesRef.current?.(externalId, uploadedMessages) ?? uploadedMessages
        );
        if (prepared.length) beginUserAction();

        const runConfig = toRunConfig(config.runConfig);
        const context = resolveContextRef.current?.();
        const payload: StreamPayload = {
          input: prepared.length ? { messages: prepared } : null,
          metadata: buildRunMetadata({
            assistantId,
            page: WORD_PAGE,
            section: sectionRef.current
          }),
          streamMode: STREAM_MODE,
          signal: config.abortSignal,
          onDisconnect: "cancel",
          ...(context !== undefined && { context }),
          ...(config.command != null && { command: config.command }),
          ...(runConfig !== undefined && { config: runConfig }),
          onRunCreated: ({ run_id }: { run_id: string }) => {
            activeRunIdRef.current = run_id;
          }
        };

        const source = filterSummarizationStream(
          dedupeUpdatesMessages(client.runs.stream(externalId, assistantId, payload)),
          {
            onCompactingChange: (isCompacting) => {
              setIsContextCompacting(isCompacting);
              if (isCompacting) compactedThreadsRef.current.add(externalId);
            },
            onCompaction: (_event, values) => resolveCompaction(externalId, values)
          }
        );

        return (async function* () {
          try {
            for await (const event of source) yield event;
            threadStateCache.invalidate(externalId);
            if (compactedThreadsRef.current.delete(externalId)) {
              resolveCompaction(externalId, null);
            }
            await onStreamCompleteRef.current?.();
          } catch (error) {
            reportRunErrorRef.current(error, {
              threadId: externalId,
              runId: activeRunIdRef.current
            });
            throw error;
          } finally {
            settle();
          }
        })();
      } catch (error) {
        settle();
        throw error;
      }
    },
    [client, assistantId, corruptedThreadId]
  );

  const threadListAdapter = useMemo(
    () =>
      createThreadListAdapter(
        client,
        assistantId,
        () => initialMetadataRef.current,
        patchThreadMetadata,
        () => threadScopeMetadataRef.current
      ),
    [client, assistantId, patchThreadMetadata]
  );

  const runtime = useLangGraphRuntime({
    unstable_allowCancellation: true,
    stream,
    adapters: { attachments: attachmentAdapter, feedback: feedbackAdapter },
    unstable_threadListAdapter: threadListAdapter,
    eventHandlers: {
      onError: (error: unknown) => {
        reportRunErrorRef.current(error, {
          threadId: activeThreadIdRef.current,
          runId: activeRunIdRef.current
        });
      }
    },
    load: async (externalId, config) => {
      const state = await client.threads.getState<AgentThreadValues>(
        externalId,
        undefined,
        { signal: config?.signal }
      );
      recordCompaction(externalId, compactionFromValues(state.values));
      const values = sanitizeSummarizationValues(state.values);
      return {
        messages: values.messages ?? [],
        interrupts: toInterrupts(state.tasks)
      };
    }
  });

  /** Stop the active run server-side too, so it cannot keep writing. */
  const cancelActiveRun = useCallback(async () => {
    const threadId = runtime.threads.mainItem.getState()?.externalId ?? null;
    const runId = activeRunIdRef.current;
    if (!threadId || !runId) return;
    try {
      await client.runs.cancel(threadId, runId);
    } catch (error) {
      console.warn("Could not cancel the active run", error);
    }
  }, [runtime, client]);

  const clearCorruptedThread = useCallback(() => setCorruptedThreadId(null), []);

  return {
    runtime,
    client,
    cancelActiveRun,
    corruptedThreadId,
    clearCorruptedThread,
    sessionExpired,
    isContextCompacting,
    compactionByThread
  };
};
